import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  AppState,
  CreditLimitSlice,
  CreditLimitRequest,
} from '../interfaces/store.interface';
import {
  CREDIT_LIMIT_INCREASE_ENDPOINT,
  CREDIT_LIMIT_INCREASE_CONTEXT,
} from '../utils/constants/apiConstants';
import { isMock, setErrorInAppState } from '../utils/helpers';
import { requestHandler } from '../utils/requestHandler';
import { mockCreditLimitSuccessResponse } from '../mocks/mockCreditLimitIncreaseResponse';

export const creditLimitIncrease = 'creditLimitIncrease';
export const initialState: CreditLimitSlice = {
  isCliLoading: false,
  selectedAccountId: '',
  isCliModalOpen: false,
  desiredAmount: '',
  annualNetIncome: '',
  cliErrorResponseCode: '',
  fullApproval: false,
  approvedAmount: '',
  previousCreditLimit: '',
  availableLimit: '',
  newCreditLimit: '',
  creditLimit: '',
  updatedAvailableBalance: '',
  isCliRequestCompleted: false,
  hasResultObtained: false,
};

const increaseRequestedCreditLimit = ({
  newCreditLimit,
  previousCreditLimit,
  availableLimit,
}: {
  newCreditLimit: string;
  previousCreditLimit: string;
  availableLimit: string;
}): string => {
  // 'approvedAmount' is new credit limit
  // 'previousCreditLimit' is previous credit limit
  // 'availableLimit' is balance pre CLI calculation
  const sanitizedCredit = parseFloat(newCreditLimit);
  const sanitizedPreviousCredit = parseFloat(previousCreditLimit);
  const sanitizedAvailableLimit = parseFloat(availableLimit);

  // credit amount needs to be a valid number greater than 0.0 and previous limit
  const isCreditValidToCalculate = newCreditLimit
    ? sanitizedCredit > sanitizedPreviousCredit
    : false;

  /* new amount is added to the current balance
      in case of approval or
      Partial approval
    */
  // New limit - previous limit
  const newAmountToAdd = isCreditValidToCalculate
    ? sanitizedCredit - sanitizedPreviousCredit
    : 0.0;

  // current balance + extra amount received as part of CLI
  const newAvailableBalance = sanitizedAvailableLimit + newAmountToAdd;

  let updatedAvailableBalance = '';
  if (newAvailableBalance <= 0) {
    updatedAvailableBalance = '0.00';
  } else {
    updatedAvailableBalance = newAvailableBalance.toFixed(2);
  }
  return updatedAvailableBalance;
};

export const submitCreditLimitRequest = createAsyncThunk(
  `${creditLimitIncrease}/submitCreditLimitRequest`,
  async (requestPayload: CreditLimitRequest, { rejectWithValue, getState }) => {
    try {
      if (isMock()) {
        return mockCreditLimitSuccessResponse;
      } else {
        const state: any = getState();
        // only in case of d2d flow promo code has value
        // otherwise it is empty and not required to be passed
        const promocode =
          state.session.d2dApiResponse?.strategyInfo?.promoCode || '';
        let payload = requestPayload;
        if (promocode) {
          payload = {
            ...requestPayload,
            promoStrategyCode: promocode,
          };
        }
        const axiosInstance = requestHandler({});
        const { data } = await axiosInstance.put(
          CREDIT_LIMIT_INCREASE_ENDPOINT,
          payload,
        );
        return data;
      }
    } catch (error: any) {
      const errorObject = error?.response || error;
      setErrorInAppState(errorObject, CREDIT_LIMIT_INCREASE_CONTEXT, false);
      return rejectWithValue({ data: error });
    }
  },
);

export const creditLimitIncreaseSlice = createSlice({
  name: creditLimitIncrease,
  initialState,
  reducers: {
    setCreditLimitAccount: (state, { payload }) => {
      state.selectedAccountId = payload.selectedAccountId;
      state.isCliModalOpen = payload.isCliModalOpen;
    },
    setCliFormValues: (state, { payload }) => {
      state.annualNetIncome = payload.annualNetIncome;
      state.desiredAmount = payload.desiredAmount;
    },
    setCreditDetails: (state, { payload }) => {
      state.availableLimit = payload.cliAvailableCredit;
      state.previousCreditLimit = payload.cliPreviousCreditLimit;
    },
    resetCliState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitCreditLimitRequest.pending, (state) => {
        state.isCliLoading = true;
        state.isCliRequestCompleted = false;
        state.hasResultObtained = false;
      })
      .addCase(
        submitCreditLimitRequest.fulfilled,
        (state, { payload }: any) => {
          state.isCliLoading = false;
          state.hasResultObtained = true;
          state.isCliRequestCompleted = true;
          state.newCreditLimit = payload.creditLimit;
          state.fullApproval = payload.fullApproval;
          state.updatedAvailableBalance = increaseRequestedCreditLimit({
            newCreditLimit: state.newCreditLimit,
            previousCreditLimit: state.previousCreditLimit,
            availableLimit: state.availableLimit,
          });
        },
      )
      .addCase(submitCreditLimitRequest.rejected, (state, { payload }: any) => {
        state.isCliLoading = false;
        state.isCliRequestCompleted = true;
        if (payload?.data.response.status === 422) {
          const businessErrorCode =
            payload.data.response.data?.businessErrors[0]?.code || '';
          if (
            businessErrorCode === '600020' ||
            businessErrorCode === '600019'
          ) {
            state.cliErrorResponseCode = businessErrorCode;
            state.hasResultObtained = true;
          } else {
            state.hasResultObtained = false;
          }
        } else {
          state.hasResultObtained = false;
        }
      });
  },
});

// state actions can be exported here
export const {
  setCreditLimitAccount,
  setCliFormValues,
  setCreditDetails,
  resetCliState,
} = creditLimitIncreaseSlice.actions;

export default creditLimitIncreaseSlice.reducer;

// state getters can be written & exported here
export const getCliModalOpenStatus = (state: AppState, accountId: string) =>
  accountId === state.creditLimitIncrease?.selectedAccountId &&
  state.creditLimitIncrease?.isCliModalOpen;
export const getCliAccountId = (state: AppState) =>
  state.creditLimitIncrease?.selectedAccountId;
export const getCreditLimitDesiredAmount = (state: AppState) =>
  state.creditLimitIncrease?.desiredAmount;
export const getCreditLimitNetIncome = (state: AppState) =>
  state.creditLimitIncrease?.annualNetIncome;
export const getCliApprovedState = (state: AppState) =>
  state.creditLimitIncrease?.fullApproval &&
  state.creditLimitIncrease?.newCreditLimit !== '';
export const getCliSuccessOrErrorStatus = (state: AppState) =>
  state.creditLimitIncrease?.isCliRequestCompleted &&
  state.creditLimitIncrease?.newCreditLimit === '';
//cliErrorResponseCode === '600019' is CLI Referred/ Under Review.
// cliErrorResponseCode === '600020' is Denied.
export const getCliResponseCodeStatus = (state: AppState) =>
  state.creditLimitIncrease?.cliErrorResponseCode === '600019';
export const getPreviousCreditLimit = (state: AppState) =>
  state.creditLimitIncrease?.previousCreditLimit;
export const getUpdatedAvailableBalance = (state: AppState) =>
  state.creditLimitIncrease?.updatedAvailableBalance;
export const getNewCreditLimit = (state: AppState) =>
  state.creditLimitIncrease?.newCreditLimit;
export const getFailureResponseStatus = (state: AppState) =>
  state.creditLimitIncrease?.hasResultObtained === false &&
  state.creditLimitIncrease?.isCliRequestCompleted;
export const getSuccessResponseStatus = (state: AppState) =>
  state.creditLimitIncrease?.hasResultObtained &&
  state.creditLimitIncrease?.isCliRequestCompleted;
export const getCliLoadingStatus = (state: AppState) =>
  state.creditLimitIncrease?.isCliLoading;
export const getAreCliValuesPopulated = (state: AppState) =>
  state.creditLimitIncrease?.annualNetIncome &&
  state.creditLimitIncrease?.desiredAmount;
